/**
 * Reading a JMC Office student-list export, as it comes, into roster rows.
 *
 * JMC exports one row per student per class (when Course Sections items are
 * included), with the student's name as "First Last", grade, school email,
 * and the class's teacher as "Mr. Boman" or "Ms. Ashley Webb". The seminar is
 * the class named SEMINAR. This keeps one row per student, their seminar
 * teacher's label, and lowercases the email (JMC keeps 27FirLas capitals).
 *
 * Teacher labels are matched to SailNorse staff by `matchTeachers`, and the
 * office confirms the matches before anything is uploaded: "Mr. Berggren"
 * could be either Berggren, and a silent wrong guess would route a whole
 * seminar's proposals to the wrong person.
 *
 * Pure, so it is tested (tests/jmc.test.ts) without real student data.
 */

export interface JmcStudent {
  email: string
  first: string
  last: string
  grade: string
  studentId: string
  /** The seminar class's teacher, as JMC writes it, or null with no seminar class. */
  teacherLabel: string | null
}

export interface JmcParse {
  students: JmcStudent[]
  /** Every distinct seminar teacher label, for matching. */
  teacherLabels: string[]
  /** Rows with no email: listed, never guessed. */
  noEmail: number
  error: string | null
}

const norm = (h: string) => h.toLowerCase().replace(/[^a-z0-9]/g, '')

/** CSV that handles quoted fields with commas, which names have. */
export function parseCsv(text: string): string[][] {
  const out: string[][] = []
  let row: string[] = []
  let cur = ''
  let q = false
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!
    if (q) {
      if (ch === '"' && text[i + 1] === '"') { cur += '"'; i++ }
      else if (ch === '"') q = false
      else cur += ch
    } else if (ch === '"') q = true
    else if (ch === ',') { row.push(cur); cur = '' }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++
      row.push(cur); out.push(row); row = []; cur = ''
    } else cur += ch
  }
  if (cur || row.length) { row.push(cur); out.push(row) }
  return out.filter((r) => r.some((c) => c.trim()))
}

export function parseJmc(text: string, seminarCourse = 'SEMINAR'): JmcParse {
  const rows = parseCsv(text.replace(/^﻿/, ''))
  const empty = (error: string): JmcParse => ({ students: [], teacherLabels: [], noEmail: 0, error })
  if (rows.length < 2) return empty('That file has no rows under the header.')
  const head = rows[0]!.map(norm)
  const col = (...names: string[]) => head.findIndex((h) => names.includes(h))
  const iName = col('flname', 'namefirstlast', 'name', 'studentname')
  const iFirst = col('firstname', 'fname', 'first')
  const iLast = col('lastname', 'lname', 'last')
  const iGrade = col('grade', 'gr')
  const iEmail = col('email', 'studentemail', 'emailaddress')
  const iTeacher = col('teacher')
  const iCourse = col('coursename', 'course')
  const iId = col('studentid', 'studentnumber', 'id', 'stateid')
  if (iEmail < 0) return empty('The file needs an Email column. Add the student email item in JMC and export again.')
  if (iName < 0 && (iFirst < 0 || iLast < 0)) return empty('The file needs the student’s name: Name (First Last), or First and Last.')

  const byEmail = new Map<string, JmcStudent>()
  let noEmail = 0
  const cell = (r: string[], i: number) => (i >= 0 ? (r[i] ?? '').trim() : '')
  for (const r of rows.slice(1)) {
    const email = cell(r, iEmail).toLowerCase()
    if (!email) { noEmail++; continue }
    let s = byEmail.get(email)
    if (!s) {
      const full = cell(r, iName)
      // "First Last": the first word is the first name, the rest the last
      // name, so "Mary Sara Lettow" needs First/Last columns or a fix after.
      const [first, ...rest] = full.split(/\s+/)
      s = {
        email,
        first: iFirst >= 0 ? cell(r, iFirst) : first ?? '',
        last: iLast >= 0 ? cell(r, iLast) : rest.join(' '),
        grade: cell(r, iGrade),
        studentId: cell(r, iId),
        teacherLabel: null,
      }
      byEmail.set(email, s)
    }
    // With a course column, only the seminar class names the seminar teacher.
    // Without one, the file is already one row per student.
    const isSeminar = iCourse < 0 || cell(r, iCourse).toLowerCase() === seminarCourse.toLowerCase()
    if (isSeminar && iTeacher >= 0 && cell(r, iTeacher) && !s.teacherLabel) s.teacherLabel = cell(r, iTeacher)
  }
  const students = [...byEmail.values()]
  const teacherLabels = [...new Set(students.map((s) => s.teacherLabel).filter((x): x is string => !!x))].sort()
  if (!students.length) return { ...empty('No students with an email were found.'), noEmail }
  return { students, teacherLabels, noEmail, error: null }
}

export interface Staff { email: string; name: string; teaches?: boolean }

/**
 * Best guess for each teacher label, or null when it cannot be told apart.
 * "Mr. Bill Heubner" names a first name; "Mrs. Heubner" then means the
 * other Heubner. A bare last name shared by two people goes to the one who
 * teaches, and if that is still a tie it is left for a person to choose.
 */
export function matchTeachers(labels: string[], staff: Staff[]): Record<string, string | null> {
  const strip = (l: string) => l.replace(/^(mr|mrs|ms|miss|mx|dr)\.?\s+/i, '').trim()
  const words = (n: string) => n.toLowerCase().split(/\s+/).filter(Boolean)
  const lastParts = (n: string) => { const w = words(n); const last = w[w.length - 1] ?? ''; return [last, ...last.split('-')] }
  const out: Record<string, string | null> = {}
  const claimed = new Set<string>()
  // Labels with a first name first: they are unambiguous, and they claim people.
  const ordered = [...labels].sort((a, b) => words(strip(b)).length - words(strip(a)).length)
  for (const label of ordered) {
    const w = words(strip(label))
    const last = w[w.length - 1] ?? ''
    let cands = staff.filter((s) => lastParts(s.name).includes(last) || words(s.name).at(-1) === last)
    if (w.length > 1) cands = cands.filter((s) => words(s.name)[0] === w[0])
    else cands = cands.filter((s) => !claimed.has(s.email))
    if (cands.length > 1) {
      const teaching = cands.filter((s) => s.teaches)
      if (teaching.length >= 1) cands = teaching
    }
    out[label] = cands.length === 1 ? cands[0]!.email : null
    if (cands.length === 1 && w.length > 1) claimed.add(cands[0]!.email)
  }
  return out
}
